import { AfterViewInit, Component, Input } from "@angular/core";
import { ModalService } from "@core-services/modal.service";
import { MasterdataService } from "@core-services/masterdata.service";
import { TranslationService } from "@core-services/translation.service";
import { TradableWalletListService } from "@main-services/wallet/tradable_wallet_list.service";
import { TradableListService } from "@main-services/currency/tradable_list.service";
import { Fiat, Field, PayoutAccount, Provider, Wallet } from "@models";
import { FiatWalletService } from "@main-services/wallet/fiat_wallet.service";
import { TransactionListDetailComponent } from "../transaction-list-detail.component";
import { FiatTransaction } from "@models/transaction";
import { AccountService } from "@modules/account/services/account.service";
import { FiatService } from "@main-services/currency";
import { ContactService } from "@main-services/contact.service";
import { PayAccountService } from "@main-services/pay_account.service";

@Component({
	selector: "fiat-transaction-list-detail",
	templateUrl: "./fiat-transaction-list-detail.component.html",
	standalone: false
})
export class FiatTransactionListDetailComponent extends TransactionListDetailComponent implements AfterViewInit {
	@Input()
	public transaction: FiatTransaction = null;

	public constructor(
		protected modalService: ModalService,
		protected tradableWalletListService: TradableWalletListService,
		protected tradableListService: TradableListService,
		protected accountService: AccountService,
		protected fiatWalletService: FiatWalletService,
		protected fiatService: FiatService,
		protected contactService: ContactService,
		protected payaccountService: PayAccountService,
		protected masterdataService: MasterdataService,
		protected translationService: TranslationService
	) {
		super(
			modalService,
			tradableWalletListService,
			tradableListService,
			fiatWalletService,
			fiatService,
			contactService,
			payaccountService,
			masterdataService
		);
	}

	public get fiat(): Fiat {
		return this.fiatService.getItemById(this.transaction.getFiatId());
	}

	public ngAfterViewInit(): void {
		if (this.getPayoutProvider()) {
			if (!this.transaction.getPayoutAccountId()) {
				return;
			}
			this.payaccountService.getAccountExtraDetails(this.transaction.getPayoutAccountId()).subscribe(() => {
				this.setPayoutAccount();
			});
		} else {
			this.setPayoutAccount();
		}

		if (this.transaction.hasDividendId()) {
			this.dividendAssetName = this.tradableListService
				.getItemById(this.transaction.getDividendAssetId())
				.getName();
		}
	}

	public isExternalTx(): boolean {
		return this.transaction.getType() === "withdrawal" || this.transaction.getType() === "deposit";
	}

	private isDeposit(): boolean {
		return this.transaction.getType() === "deposit";
	}

	public isCardDeposit(): boolean {
		return this.isCreditCard() && this.isDeposit();
	}

	public isCardWithdrawal(): boolean {
		return this.isCreditCard() && this.isWithdrawal();
	}

	public getCardHolderName(): string {
		if (!this.payoutAccount) {
			return null;
		}

		return this.payoutAccount?.getField("card_holder").getValue();
	}

	public getCardMask(): string {
		if (!this.payoutAccount) {
			return null;
		}

		return this.payoutAccount?.getField("card_mask").getValue();
	}

	public isCreditCard(): boolean {
		return this.transaction.getCCardDigits().length > 0 || !!this.payoutAccount?.getField("card_mask");
	}

	public getFromWallet(): Wallet {
		if (this.transaction.getType() === "withdrawal" || this.transaction.getType() === "refund") {
			return this.fiatWallet;
		}
		return super.getFromWallet();
	}

	public getToWallet(): Wallet {
		if (this.transaction.getType() === "deposit" || this.transaction.getType() === "refund") {
			return this.fiatWallet;
		}
		return super.getToWallet();
	}

	public getFromWalletName(): string {
		if (this.transaction.getType() === "deposit") {
			if (this.transaction.hasBankAccountDetails) {
				return this.transaction.getBankAccountDetails().getBankName();
			}
			return this.translationService.translate(
				this.masterdataService.getPaymentOptionById(this.transaction.getPaymentOptionId())?.getShortName() +
					"_primary_field"
			);
		}
		if (this.transaction.getType() === "transfer" && this.transaction.isOutgoing()) {
			return this.transaction.getFrom();
		}
		return super.getFromWalletName();
	}

	public getToWalletName(): string {
		if (this.transaction.getType() === "withdrawal" && this.payoutAccount) {
			if (this.transaction.hasBankAccountDetails) {
				return this.transaction.getBankAccountDetails().getBankName();
			}
			if (!this.isBankTransfer() && !this.isFiatSend()) {
				return this.masterdataService.getPayoutProviderById(this.payoutAccount.getPayoutProviderId()).getName();
			}
			return this.getBankName();
		}
		return super.getToWalletName();
	}

	public getBankName(): string {
		if (this.isBankTransfer() || this.isFiatSend()) {
			return (this.payoutAccount.getFields().find((f) => f.getName() === "bank_name") || new Field()).getValue();
		}
		return "";
	}

	public getDividendAssetName(): string {
		return this.dividendAssetName;
	}

	public getCryptoAmount(): string {
		if (this.transaction.hasTrade()) {
			return this.transaction.getTrade().getAmountTradable() + "";
		}
		return "";
	}

	public getPayAccountIban(): string {
		if (this.transaction.hasBankAccountDetails) {
			return this.transaction.getBankAccountDetails().getIban();
		}
		if (this.payoutAccount) {
			return (this.payoutAccount.getFields().find((f) => f.getName() === "iban") || new Field()).getValue();
		}
		return "";
	}

	public getPayAccountBic(): string {
		if (this.transaction.hasBankAccountDetails) {
			return this.transaction.getBankAccountDetails().getBic();
		}
		if (this.payoutAccount) {
			return (this.payoutAccount.getFields().find((f) => f.getName() === "bic") || new Field()).getValue();
		}
		return "";
	}

	public showBankDetails(): boolean {
		return this.isBankTransfer() || this.isFiatSend() || this.transaction.hasBankAccountDetails;
	}

	public logoUrl(p: PayoutAccount | Provider): string {
		return this.masterdataService.logoUrl(p);
	}

	private setPayoutAccount(): void {
		if (this.transaction instanceof FiatTransaction) {
			this.payoutAccount = this.payaccountService.list.find(
				(a) => a._id === this.transaction.getPayoutAccountId()
			);
		}
	}

	public getVsnFeeAmount(): string {
		if (!this.transaction.hasTrade() || !this.transaction.isVsnFeeCollection()) {
			return "0";
		}
		return this.transaction.getTrade().getVsnFeeCollection().attributes.vsn_fee_amount;
	}

	public getVsnFeeRate(): string {
		if (!this.transaction?.hasTrade() || !this.transaction.isVsnFeeCollection()) {
			return "0";
		}

		const trade = this.transaction.getTrade();
		const vsnCollection = trade?.getVsnFeeCollection();
		if (!vsnCollection) {
			return "0";
		}

		return vsnCollection.attributes.vsn_fee_rate || "0";
	}
}
